import { useState, useRef } from 'react'
import { useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import Select from 'react-select'
import Editor from '../Editor/Editor'
import Heading from '../Heading/Heading'
import Button from '../Button/Button'
import createZendeskTicket from '../../graphql/createZendeskTicket'
import createPriority from '../../graphql/createPriority'
import useAuth from '../../hooks/useAuth'
import useListStatuses from '../../hooks/useListStatuses'
import { quillIndent, htmlToText } from '../../utils'

const Wrapper = styled.div`
  padding-right: 45px;
  margin-bottom: 44px;

  @media only screen and (max-width: 920px) {
    padding: 0 18px;
  }
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`

const FullWidth = styled.div`
  grid-column: 1 / -1;
`

const Input = styled.input`
  border: 2px solid var(--color-text-secondary);
  background-color: var(--color-bg-input);
  padding: 12px 16px;
  border-radius: 8px;
  color: var(--color-input-text, #4b5882);
  font-size: 14px;
  font-family: 'Roboto', Arial, sans-serif;
  font-weight: 500;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

  &::placeholder {
    color: var(--color-text-secondary);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(151, 202, 111, 0.2);
    background-color: #fff;
  }

  &:hover {
    border-color: var(--color-primary);
  }

  &:disabled {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--color-text-disabled);
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const Textarea = styled.textarea`
  border: 2px solid var(--color-text-secondary);
  background-color: var(--color-bg-input);
  padding: 12px 16px;
  border-radius: 8px;
  color: var(--color-input-text, #4b5882);
  font-size: 14px;
  font-family: 'Roboto', Arial, sans-serif;
  font-weight: 500;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  max-width: 100%;
  resize: vertical;

  &::placeholder {
    color: var(--color-text-secondary);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(151, 202, 111, 0.2);
    background-color: #fff;
  }

  &:hover {
    border-color: var(--color-primary);
  }

  &:disabled {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--color-text-disabled);
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  font-family: 'Roboto', Arial, sans-serif;
  margin-bottom: 8px;
  color: var(--color-text-primary);
  display: block;
`

const Note = styled.span`
  font-size: 12px;
  font-family: 'Roboto', Arial, sans-serif;
  margin-top: 8px;
  color: var(--color-text-secondary);
  display: block;
`

const ErrorMsg = styled.span`
  color: var(--color-error);
  font-size: 12px;
  font-family: 'Roboto', Arial, sans-serif;
  font-weight: 500;
  margin-top: 8px;
  display: block;
`

const Box = styled.div`
  margin-top: 35px;
  margin-bottom: 20px;
  padding: 24px 20px;
  border-radius: 8px;
  background-color: var(--color-bg-card);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 20px;

  @media only screen and (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`

const StyledSelect = styled(Select)`
  z-index: 99;

  .react-select {
    &__menu {
      position: absolute;
    }

    &__option {
      background-color: var(--color-bg-card, #2a2a2a);
      color: var(--color-text-primary);

      &:hover {
        background-color: var(--color-bg-sidebar, #3a3a3a);
      }

      &:first-child {
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
      }

      &:last-child {
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
      }
    }
  }
`

const selectTypographyStyles = {
  color: 'var(--color-text-secondary, #a5acc2)',
  fontSize: '14px',
  fontWeight: '500',
}

const customSelectStyles = {
  option: (provided, state) => ({
    ...provided,
    ...selectTypographyStyles,
    backgroundColor: state.isSelected ? 'var(--color-bg-sidebar, #3a3a3a)' : 'var(--color-bg-card, #2a2a2a)',
    padding: '12px',
    cursor: 'pointer',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (provided) => ({
    ...provided,
    marginTop: '2px',
    color: 'var(--color-text-secondary)',
  }),
  singleValue: (provided) => ({
    ...provided,
    ...selectTypographyStyles,
    whiteSpace: 'nowrap',
  }),
  placeholder: (provided) => ({
    ...provided,
    ...selectTypographyStyles,
    whiteSpace: 'nowrap',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '4px',
    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.2), 0 -2px 8px 0 rgba(0,0,0,0.2)',
    marginTop: '8px',
    backgroundColor: 'var(--color-bg-card, #2a2a2a)',
    border: '1px solid var(--color-border, rgba(255,255,255,0.1))',
  }),
  menuList: (provided) => ({
    ...provided,
    overflowY: 'visible',
    borderRadius: '4px',
    paddingTop: 0,
    paddingBottom: 0,
  }),
  control: (provided, state) => ({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 8px',
    height: '44px',
    border: state.isFocused ? '2px solid var(--color-primary)' : '2px solid var(--color-text-secondary, #a5acc2)',
    borderRadius: '8px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    backgroundColor: 'var(--color-bg-input)',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(151, 202, 111, 0.2)' : 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      borderColor: 'var(--color-primary)',
    },
  }),
}

const NewSupportCase = () => {
  const { selectedOrg } = useAuth()
  const history = useHistory()
  const [createTicket] = useMutation(createZendeskTicket)
  const [createTask] = useMutation(createPriority)
  const editorRef = useRef()
  const listListatuses = useListStatuses()

  const [form, setForm] = useState({
    values: {
      comment: '',
      priority: null,
      cc: '',
      subject: '',
    },
    touched: {},
    errors: {},
  })
  const [isSubmitting, setSubmitting] = useState(false)
  const isTeamSupportSelected =  form.values.priority?.value === 'Support'

  const validate = values => {
    const errors = {}

    if (!values.comment) errors.comment = 'Please fill comment field!'
    if (!values.subject) errors.subject = 'Please fill subject field!'

    return errors
  }

  const onChange = (name, value) => {
    const isSelecting = name === 'priority'

    setForm(form => {
      const values = {
        ...form.values,
        [name]: value,
        ...isSelecting && value.value === 'Support' && {
          comment: htmlToText(form.values.comment)
        },
      }

      const errors = validate(values)

      const newForm = {
        values,
        touched: {
          ...form.touched,
          [name]: true,
        },
        errors,
      }

      return newForm
    })

    if (
        isSelecting
        && value.value !== 'Support'
        && isTeamSupportSelected
      ) editorRef.current.setText(form.values.comment)
  }

  const handleChange = e => {
    const { name, value } = e.target

    onChange(name, value)
  }

  const onSubmit = async () => {
    try {
      const errors = validate(form.values)

      if (!!Object.keys(errors).length) {
        const touched = Object.keys(errors).reduce((obj, key) => {
          obj[key] = true
          return obj
        }, {})

        setForm(form => ({
          ...form,
          errors,
          touched,
        }))
        return
      }

      setSubmitting(true)

      const variables = isTeamSupportSelected
        ? {
            organizationId: selectedOrg.value,
            issue: {
              title: form.values.subject,
              status: listListatuses.find(status => status.type === 'backlog')?.name,
              description: form.values.comment
            },
          }
        : {
            ...form.values,
            priority: form.values.priority ? form.values.priority.value : undefined,
            cc: form.values.cc.split(',').map(i => i.trim()),
            organizationId: selectedOrg.value,
            comment: quillIndent(form.values.comment)
          }

      if (!isTeamSupportSelected) {
        // Add this to every comment that is made
        variables.comment += '<br><span style="color:white">System note: sent from Harbor ref-46947</span>'
      }

      const { data } = isTeamSupportSelected
        ? await createTask({ variables, context: { version: 4 } })
        : await createTicket({ variables })

      const redirectPath = isTeamSupportSelected
        ? `/priority-board/${data.createIssue.id}`
        : `/support-cases/${data.createZendeskTicket.id}`

      history.push(redirectPath)
    } catch (err) {
      console.log(err)
    } finally {
      setSubmitting(false)
    }
  }

  const subjectError = form.touched.subject ? form.errors.subject : ''
  const commentError = form.touched.comment ? form.errors.comment : ''

  const priorityOptions = [
    {
      value: 'Normal',
      label: 'Normal',
      description: 'One business day reply'
    },
    {
      value: 'High',
      label: 'High',
      description: 'Four hour, 24x7 response',
    },
    {
      value: 'Urgent',
      label: 'Urgent',
      description: '15 min, 24x7 response (site down, production impaired)',
    },
    {
      value: 'Support',
      label: 'Team Support Backlog',
      description: 'To be reviewed with Team Support'
    },
  ]

  return (
    <Wrapper>
      <Heading>Support Cases - New Case</Heading>
      <Box>
        {/* Subject - Full Width */}
        <FullWidth>
          <FieldGroup>
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              name="subject"
              value={form.values.subject}
              onChange={handleChange}
              autoComplete="off"
              aria-invalid={!!subjectError}
              aria-describedby={subjectError ? 'subject-error' : undefined}
            />
            {!!subjectError && (
              <ErrorMsg id="subject-error" role="alert" data-testid="subject-error">
                {subjectError}
              </ErrorMsg>
            )}
          </FieldGroup>
        </FullWidth>

        {/* Priority - Left Column */}
        <FieldGroup>
          <Label htmlFor="priority" id="priority-label">Priority</Label>
          <StyledSelect
            inputId="priority"
            aria-label="Priority level"
            aria-labelledby="priority-label"
            classNamePrefix="react-select"
            options={priorityOptions}
            value={form.values.priority}
            onChange={value => onChange('priority', value)}
            placeholder="Select priority…"
            styles={customSelectStyles}
            isSearchable={false}
            getOptionLabel={(option) => option.label}
            formatOptionLabel={(option) => (
              <span title={option.description}>{option.label}</span>
            )}
          />
        </FieldGroup>

        {/* CC - Right Column (only when not Team Support) */}
        {!isTeamSupportSelected ? (
          <FieldGroup>
            <Label htmlFor="cc">CC (Optional)</Label>
            <Input
              id="cc"
              name="cc"
              type="email"
              inputMode="email"
              spellCheck={false}
              placeholder="Add emails…"
              autoComplete="email"
              value={form.values.cc}
              onChange={handleChange}
            />
            <Note>Enter e-mail addresses separated by comma.</Note>
          </FieldGroup>
        ) : (
          <div /> /* Empty placeholder to maintain grid */
        )}

        {/* Description - Full Width */}
        <FullWidth>
          <FieldGroup>
            <Label htmlFor="comment">Description</Label>
            <Editor
              style={{ display: isTeamSupportSelected ? 'none' : 'block' }}
              onChange={(value, _, editor) => {
                editorRef.current = editor
                onChange('comment', value, editor)
              }}
            />
            {isTeamSupportSelected && (
              <Textarea
                id="comment"
                name="comment"
                value={form.values.comment}
                rows="4"
                placeholder="Enter description here…"
                onChange={handleChange}
                aria-invalid={!!commentError}
                aria-describedby={commentError ? 'comment-error' : 'description-note'}
              />
            )}
            {!!commentError && (
              <ErrorMsg id="comment-error" role="alert" data-testid="comment-error">
                {commentError}
              </ErrorMsg>
            )}
            <Note id="description-note">
              Please enter the details of your request. A member of our support
              staff will respond as soon as possible.
            </Note>
          </FieldGroup>
        </FullWidth>
      </Box>
      <Button disabled={isSubmitting} isSubmitting={isSubmitting} onClick={onSubmit}>
        Submit
      </Button>
    </Wrapper>
  )
}

export default NewSupportCase
